import type { Product, Category, Seller, PaymentMethod, Order, StoreConfig, PaginatedResponse, AuthUser } from '../types/api';

const FALLBACK_API_PORT = '3333';

function getApiBase(): string {
  const configured = import.meta.env.VITE_API_URL || '';

  if (import.meta.env.DEV) {
    return configured.replace(/\/$/, '') || '';
  }

  if (configured && typeof window !== 'undefined') {
    try {
      const parsed = new URL(configured, window.location.origin);
      const localHost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
      const currentHostLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      if (localHost && !currentHostLocal) {
        return `${window.location.protocol}//${window.location.hostname}:${parsed.port || FALLBACK_API_PORT}`;
      }

      return `${parsed.origin}${parsed.pathname === '/' ? '' : parsed.pathname}`.replace(/\/$/, '');
    } catch {
      return configured.replace(/\/$/, '');
    }
  }

  if (typeof window !== 'undefined') {
    return configured === '/' ? '' : configured.replace(/\/$/, '');
  }

  return configured.replace(/\/$/, '');
}

const API_BASE = getApiBase();

function resolveUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    if (typeof window !== 'undefined') {
      try {
        const parsed = new URL(url);
        const localHost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
        const currentHostLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

        if (localHost && !currentHostLocal) {
          return `${window.location.protocol}//${window.location.hostname}:${parsed.port}${parsed.pathname}${parsed.search}${parsed.hash}`;
        }
      } catch {
        return url;
      }
    }

    return url;
  }
  if (url.startsWith('data:')) return url;

  if (import.meta.env.DEV && typeof window !== 'undefined' && url.startsWith('/api/')) {
    return `${window.location.protocol}//${window.location.hostname}:${FALLBACK_API_PORT}${url}`;
  }

  return `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
}

function resolveProductImage<T extends { image: string }>(product: T): T {
  return { ...product, image: resolveUrl(product.image) };
}

export function resolveApiUrl(url: string): string {
  return resolveUrl(url);
}

function getToken(): string | null {
  return localStorage.getItem('megax_token');
}

function getPanelToken(): string | null {
  return localStorage.getItem('megax_panel_token');
}

export function hasPanelToken(): boolean {
  return getPanelToken() !== null;
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function panelHeaders(): Record<string, string> {
  const token = getPanelToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: 'Erro de conexao' }));
    throw new Error(body.message || `Erro ${res.status}`);
  }
  return res.json();
}

export const api = {
  // === PUBLIC ===
  getStoreConfig(): Promise<StoreConfig> {
    return fetch(`${API_BASE}/api/public/store`)
      .then(r => handleResponse<StoreConfig>(r))
      .then(data => ({ ...data, logo: resolveUrl(data.logo) }));
  },

  updateStoreConfig(data: { name: string; logoUrl: string; phone: string; commercialMessage: string; primaryColor: string }): Promise<StoreConfig> {
    return fetch(`${API_BASE}/api/store`, {
      method: 'PUT',
      headers: panelHeaders(),
      body: JSON.stringify(data),
    })
      .then(r => handleResponse<StoreConfig>(r))
      .then(cfg => ({ ...cfg, logo: resolveUrl(cfg.logo) }));
  },

  saveCustomerAccess(data: { cnpj: string; name: string; password: string; tradeName?: string; email?: string; phone?: string }): Promise<{ customer: { id: string; cnpj: string; name: string }; message: string }> {
    const token = getPanelToken();
    if (!token) {
      throw new Error('Sessao do painel ausente. Entre novamente para salvar o acesso do cliente.');
    }

    return fetch(`${API_BASE}/api/panel/customers/access`, {
      method: 'POST',
      headers: panelHeaders(),
      body: JSON.stringify(data),
    }).then(r => handleResponse<{ customer: { id: string; cnpj: string; name: string }; message: string }>(r));
  },

  panelLogin(user: string, password: string): Promise<{ token: string; user: { user: string } }> {
    return fetch(`${API_BASE}/api/panel/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user, password }),
    }).then(r => handleResponse<{ token: string; user: { user: string } }>(r));
  },

  getPanelMe(): Promise<{ user: { user: string } }> {
    return fetch(`${API_BASE}/api/panel/me`, { headers: panelHeaders() }).then(r => handleResponse<{ user: { user: string } }>(r));
  },

  getPanelOrders(status?: string): Promise<Array<{ id: string; orderNumber: number; status: string; customerName: string; customerCnpj: string; sellerName?: string | null; total: number; notes: string; createdAt: string; importedAt?: string | null; items: Array<{ productId: string; productName: string; quantity: number; unitPrice: number; total: number }> }>> {
    const query = new URLSearchParams();
    if (status) query.set('status', status);
    return fetch(`${API_BASE}/api/panel/orders?${query}`, { headers: panelHeaders(), cache: 'no-store' }).then(r => handleResponse<any[]>(r));
  },

  getPanelCustomers(): Promise<Array<{ id: string; cnpj: string; name: string; tradeName?: string | null; email?: string | null; phone?: string | null; active: boolean; lastLoginAt?: string | null; ordersCount: number }>> {
    return fetch(`${API_BASE}/api/panel/customers`, { headers: panelHeaders() }).then(r => handleResponse<any[]>(r));
  },

  getPanelSellers(): Promise<Seller[]> {
    return fetch(`${API_BASE}/api/sellers`, { headers: panelHeaders() }).then(r => handleResponse<Seller[]>(r));
  },

  setPanelToken(token: string): void {
    localStorage.setItem('megax_panel_token', token);
  },

  clearPanelToken(): void {
    localStorage.removeItem('megax_panel_token');
  },

  getCategories(): Promise<Category[]> {
    return fetch(`${API_BASE}/api/public/categories`).then(r => handleResponse<Category[]>(r));
  },

  getPublicProducts(params?: { q?: string; category?: string; categoryId?: number; page?: number; limit?: number }): Promise<PaginatedResponse<Product>> {
    const query = new URLSearchParams();
    if (params?.q) query.set('q', params.q);
    if (params?.category) query.set('category', params.category);
    if (params?.categoryId !== undefined) query.set('categoryId', String(params.categoryId));
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    return fetch(`${API_BASE}/api/public/products?${query}`)
      .then(r => handleResponse<PaginatedResponse<Product>>(r))
      .then(response => ({
        ...response,
        data: response.data.map(resolveProductImage),
      }));
  },

  getPublicProduct(id: string): Promise<Product> {
    return fetch(`${API_BASE}/api/public/products/${id}`)
      .then(r => handleResponse<Product>(r))
      .then(resolveProductImage);
  },

  // === AUTH ===
  login(cnpj: string, password: string): Promise<{ token: string; user: AuthUser }> {
    return fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cnpj, password }),
    }).then(r => handleResponse<{ token: string; user: AuthUser }>(r));
  },

  getMe(): Promise<{ user: AuthUser }> {
    return fetch(`${API_BASE}/api/auth/me`, { headers: authHeaders() }).then(r => handleResponse<{ user: AuthUser }>(r));
  },

  // === AUTHENTICATED ===
  getProducts(params?: { q?: string; category?: string; categoryId?: number; page?: number; limit?: number }): Promise<PaginatedResponse<Product>> {
    const query = new URLSearchParams();
    if (params?.q) query.set('q', params.q);
    if (params?.category) query.set('category', params.category);
    if (params?.categoryId !== undefined) query.set('categoryId', String(params.categoryId));
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    return fetch(`${API_BASE}/api/products?${query}`, { headers: authHeaders() })
      .then(r => handleResponse<PaginatedResponse<Product>>(r))
      .then(response => ({
        ...response,
        data: response.data.map(resolveProductImage),
      }));
  },

  getSellers(): Promise<Seller[]> {
    return fetch(`${API_BASE}/api/sellers`, { headers: authHeaders() }).then(r => handleResponse<Seller[]>(r));
  },

  getPaymentMethods(): Promise<PaymentMethod[]> {
    return fetch(`${API_BASE}/api/payment-methods`, { headers: authHeaders() }).then(r => handleResponse<PaymentMethod[]>(r));
  },

  createOrder(data: { sellerId: string; paymentMethodId: string; items: { productId: string; quantity: number }[]; notes?: string }): Promise<Order> {
    return fetch(`${API_BASE}/api/orders`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    }).then(r => handleResponse<Order>(r));
  },

  getMyOrders(): Promise<Order[]> {
    return fetch(`${API_BASE}/api/orders/me`, { headers: authHeaders(), cache: 'no-store' }).then(r => handleResponse<Order[]>(r));
  },
};
